import axios, { AxiosInstance } from 'axios';
import { detectRegion } from '../config/regionalConfig';
import { resolveAuthoritativeApiBaseUrl } from '../config/api.config';

const api: AxiosInstance = axios.create({
  baseURL: resolveAuthoritativeApiBaseUrl(),
  withCredentials: true,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// console.debug('[Dashboard API] Configured Base URL:', api.defaults.baseURL);

let getVaultTokenHandler: (() => string | null) | null = null;

export const setVaultTokenGetter = (getter: (() => string | null) | null) => {
  getVaultTokenHandler = getter;
};

// Add auth token
api.interceptors.request.use((config) => {
  // Dynamically resolve authoritative base URL for regional gateway routing (STORY-EPIC-75.2)
  config.baseURL = resolveAuthoritativeApiBaseUrl();

  // Inject Region Header (ADR-001 Dynamic)
  const region = detectRegion();
  const tokenKey = `admin_token_${region.toLowerCase()}`;
  const token = localStorage.getItem(tokenKey) || localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['x-region'] = region;

  // Inject in-memory Vault Session Token if active (Zero LocalStorage persistence)
  if (getVaultTokenHandler) {
    const vaultToken = getVaultTokenHandler();
    if (vaultToken) {
      config.headers['X-Vault-Session-Token'] = vaultToken;
    }
  }

  return config;
});

// Response Handler
api.interceptors.response.use(
  (response) => {
    // Detect unexpected HTML responses (e.g. Vite SPA fallback on API routes)
    if (typeof response.data === 'string' && (response.data.trim().startsWith('<!doctype html>') || response.data.trim().startsWith('<html'))) {
      const url = response.config.url || '';
      console.error(`[API Client] Received HTML document instead of JSON for API route "${url}". Vite reverse proxy for /api may be missing or unconfigured.`);
      return Promise.reject(new Error(`API route "${url}" returned HTML (SPA fallback) instead of JSON. Check reverse proxy.`));
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we are not already at login
      if (!window.location.pathname.includes('/login')) {
        const region = detectRegion();
        localStorage.removeItem(`admin_token_${region.toLowerCase()}`);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const login = (credentials: { email: string; password: string }) => {
  return api.post('/admin/auth/login', credentials);
};

export const getProfile = () => {
  return api.get('/admin/auth/me');
};

export default api;